import React, { useMemo, useState } from 'react'
import { Formik, Form } from 'formik'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { useMutation } from '@apollo/client/react'
import { Button } from '../Button'
import { ModalContainer, ModalWindow, ModalWindowContent, ModalHeader } from '../layout'
import { TextInput, TextAreaInput } from '../layout/form/input'
import { setSuccessNotification } from '../notification'
import {
  setErrorNotifications,
  setFatalErrorNotification,
} from '../responses/setErrorNotifications'
import { useSettings } from '../../containers/Parameters'
import { buildSmsPrefill } from './buildSmsPrefill'
import { formatMessageSafe } from '../../util/translateInputOptions'
import { messages } from './messages'
import {
  SEND_TOWING_ORDER_SMS,
  SEND_TOWING_RECORD_SMS,
  SendTowingOrderSmsMutation,
  SendTowingOrderSmsMutationVariables,
  SendTowingRecordSmsMutation,
  SendTowingRecordSmsMutationVariables,
} from './queries'

interface Props {
  entityType: 'order' | 'record'
  entityId: number
  ordererPhone?: string | null
  onClose: () => void
}

interface FormValues {
  phone: string
  message: string
}

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
`

const PhoneRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;

  > div:first-child {
    flex: 1;
    min-width: 0;
  }
`

const CharCounter = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.grey};
  text-align: right;
`

const ValidationErrorText = styled.p`
  color: #c62828;
  margin-top: 0.25rem;
  font-size: 0.8rem;
`

const SendButton = styled(Button)`
  && {
    width: 100% !important;
    background-color: #2e7d32;
    border-color: #2e7d32;

    &:hover:not(:disabled) {
      background-color: #1b5e20;
      border-color: #1b5e20;
    }

    &:disabled {
      background-color: #999999;
      border-color: #999999;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`

const CancelButton = styled(Button)`
  && {
    width: 100% !important;
    background-color: #c62828;
    border-color: #c62828;
    color: ${props => props.theme.colors.staticWhite};
    &:hover:not(:disabled) {
      background-color: #b71c1c;
      border-color: #b71c1c;
    }
    &:disabled {
      background-color: #999999;
      border-color: #999999;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;

  > div {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    max-width: 300px;
    margin: 0 auto;

    > div {
      min-width: auto;
      width: 100%;
    }
  }
`

function getSmsCharCount(message: string) {
  const len = message.length
  return { count: len, segments: Math.ceil(len / 160) || 0 }
}

export const SendSmsDialog: React.FC<Props> = ({ entityType, entityId, ordererPhone, onClose }) => {
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const settings = useSettings()
  const [validationError, setValidationError] = useState<string | null>(null)

  const initialMessage = useMemo(
    () => buildSmsPrefill(settings?.sms?.greeting, settings?.sms?.footer),
    [settings?.sms?.greeting, settings?.sms?.footer]
  )

  const initialValues: FormValues = {
    phone: '',
    message: initialMessage,
  }

  const handleSuccess = (message?: string | null) => {
    setSuccessNotification(
      formatMessage(messages.sendSuccessTitle),
      message || formatMessage(messages.sendSuccessMessage)
    )
    onClose()
  }

  const [sendOrderSms, { loading: orderLoading }] = useMutation<
    SendTowingOrderSmsMutation,
    SendTowingOrderSmsMutationVariables
  >(SEND_TOWING_ORDER_SMS, {
    onCompleted({ sendTowingOrderSms }) {
      if (sendTowingOrderSms.__typename === 'SendTowingOrderSmsSuccess') {
        handleSuccess(sendTowingOrderSms.message)
      } else {
        setErrorNotifications({ data: sendTowingOrderSms })
      }
    },
    onError(error) {
      setFatalErrorNotification(error)
    },
  })

  const [sendRecordSms, { loading: recordLoading }] = useMutation<
    SendTowingRecordSmsMutation,
    SendTowingRecordSmsMutationVariables
  >(SEND_TOWING_RECORD_SMS, {
    onCompleted({ sendTowingRecordSms }) {
      if (sendTowingRecordSms.__typename === 'SendTowingRecordSmsSuccess') {
        handleSuccess(sendTowingRecordSms.message)
      } else {
        setErrorNotifications({ data: sendTowingRecordSms })
      }
    },
    onError(error) {
      setFatalErrorNotification(error)
    },
  })

  const loading = orderLoading || recordLoading

  const handleSubmit = (values: FormValues) => {
    const phone = values.phone.trim()
    const message = values.message.trim()

    if (entityType === 'order') {
      sendOrderSms({ variables: { input: { towingOrderId: entityId, phone, message } } })
    } else {
      sendRecordSms({ variables: { input: { towingRecordId: entityId, phone, message } } })
    }
  }

  const fillPhoneFromOrderer = (setFieldValue: (field: string, value: string) => void) => {
    setValidationError(null)
    const phone = (ordererPhone || '').trim()
    if (phone) {
      setFieldValue('phone', phone)
    } else {
      setValidationError(formatMessage(messages.noOrdererPhoneError))
    }
  }

  return (
    <ModalContainer backgroundColor="rgba(0, 0, 0, 0.6)">
      <ModalWindow>
        <ModalHeader>{formatMessage(messages.header)}</ModalHeader>
        <ModalWindowContent>
          <Formik 
            initialValues={initialValues} 
            onSubmit={handleSubmit} 
            enableReinitialize
          >
            {({ values, setFieldValue }) => {
              const charCount = getSmsCharCount(values.message)
              return (
                <Form>
                  <FormSection>
                    <PhoneRow style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5em' }}>
                      <TextInput
                        name="phone"
                        label={formatMessage(messages.phoneLabel)}
                        type="tel"
                        required
                      />
                      <Button
                        category="on"
                        label={formatMessage(messages.ordererLabel)}
                        size="s"
                        maxWidth="30%"
                        fontWeight="normal"
                        onClick={() => fillPhoneFromOrderer(setFieldValue)}
                      />
                    </PhoneRow>
             
                  </FormSection>

                  <FormSection>
                    <TextAreaInput
                      name="message"
                      label={formatMessage(messages.messageLabel)}
                      required
                    />
                    <CharCounter>
                      {charCount.count} / {charCount.segments}
                    </CharCounter>
                  </FormSection>

                  {validationError && <ValidationErrorText>{validationError}</ValidationErrorText>}

                  <ButtonContainer>
                    <div>
                      <SendButton
                        submit
                        label={formatMessage(messages.sendLabel)}
                        category="on"
                        size="m"
                        fontWeight="normal"
                        loading={loading}
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <CancelButton
                        label={formatMessage(messages.cancelLabel)}
                        category="off"
                        size="m"
                        fontWeight="normal"
                        onClick={onClose}
                        disabled={loading}
                      />
                    </div>
                  </ButtonContainer>
                </Form>
              )
            }}
          </Formik>
        </ModalWindowContent>
      </ModalWindow>
    </ModalContainer>
  )
}
